import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon, Popper } from '@sb/design-system';

import {
  CHAT_ICON_BUTTON_CLASS,
  CHAT_ICON_BUTTON_STYLE,
} from './chatIconButton';

/**
 * A standing configuration problem with the agent on this robot — something
 * the operator can go and fix. Per-turn failures are not warnings in this
 * sense: they stay inline with the message that produced them.
 */
export interface ChatWarning {
  /** Stable across renders; also the React key. */
  id: string;
  message: string;
}

// A pointer crossing the header on its way to the kebab shouldn't flash the
// panel open, and after it leaves the icon it needs time to travel into the
// panel without it closing underneath.
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 250;

/**
 * Warning icon for the chat header, with the warnings themselves in a popover
 * beside it. Renders nothing when there is nothing to warn about, so the
 * icon's presence is itself the signal.
 *
 * A mouse opens it by hovering, touch by tapping, and a keyboard by pressing
 * the focused button. The design-system `Tooltip` has those mechanics already
 * but its content box is a fixed 200px of centred text, which cannot hold
 * several warnings separated by dividers.
 */
export function ChatWarnings({ warnings }: { warnings: ChatWarning[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const setOpenAfter = useCallback((open: boolean, delayMS: number) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsOpen(open), delayMS);
  }, []);

  const setOpenNow = useCallback((open: boolean) => {
    clearTimeout(timerRef.current);
    setIsOpen(open);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const hasWarnings = warnings.length > 0;

  // The trigger unmounts when the last warning clears (e.g. the operator fixed
  // the override mid-session); the open state must not outlive it and reopen
  // the panel if a warning returns.
  useEffect(() => {
    if (!hasWarnings) setIsOpen(false);
  }, [hasWarnings]);

  if (!hasWarnings) return null;

  return (
    <Popper
      isOpen={isOpen}
      onClose={() => setOpenNow(false)}
      placement="bottom-end"
      offset={4}
      className="tw-flex"
      content={
        <div
          role="status"
          data-testid="chat-warnings-popover"
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') setOpenNow(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') {
              setOpenAfter(false, CLOSE_DELAY_MS);
            }
          }}
          className={cx('tw-flex', 'tw-flex-col', 'tw-text-13')}
          style={{
            // 280px, 12px radius, the layered shadow and the divider border
            // match ChatPopoverMenu, which has no tokens for them either (the
            // radius scale jumps 10 → 14).
            width: 280,
            background: 'var(--surface-primary-elevated)',
            color: 'var(--label-primary)',
            borderRadius: 12,
            boxShadow:
              '0 12px 32px -8px rgba(0,0,0,0.18), 0 4px 12px -4px rgba(0,0,0,0.10)',
            border: '1px solid var(--divider-primary)',
          }}
        >
          {warnings.map((warning, index) => (
            <p
              key={warning.id}
              data-testid="chat-warnings-item"
              className={cx(
                'tw-px-12',
                'tw-py-10',
                index > 0 && 'tw-border-t tw-border-divider-primary',
              )}
            >
              {warning.message}
            </p>
          ))}
        </div>
      }
    >
      <button
        type="button"
        aria-label={
          warnings.length === 1
            ? '1 agent warning'
            : `${warnings.length} agent warnings`
        }
        aria-expanded={isOpen}
        data-testid="chat-warnings-button"
        className={CHAT_ICON_BUTTON_CLASS}
        style={CHAT_ICON_BUTTON_STYLE}
        onClick={() => setOpenNow(!isOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpenNow(false);
        }}
        // Hover is the mouse affordance only; touch and keyboard go through
        // the click handler, which fires for a tap and for Enter / Space.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setOpenAfter(true, OPEN_DELAY_MS);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') {
            setOpenAfter(false, CLOSE_DELAY_MS);
          }
        }}
      >
        <Icon kind="exclamationTriangle" className="tw-icon-22 tw-text-mango" />
      </button>
    </Popper>
  );
}
